import React, { useEffect, useState } from 'react';
import useApi from '../api';
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface MessageSearchResult {
  id: string;
  message_id: string;
  query?: string;
  search_query?: string;
  system_prompt?: string;
  search_results?: any;
  page_text?: string;
  qualify_prompt?: string;
  ready_to_book?: boolean;
  location_enabled?: boolean;
  distances?: any;
  eval_score?: any;
  created_at?: string;
}

interface LegacySearchResultsProps {
  messageId: string;
}

const renderJsonData = (data: any, title: string) => {
  if (!data) return null;

  return (
    <div className="space-y-2">
      <div className="text-sm font-medium">{title}</div>
      <pre className="text-xs bg-muted p-3 rounded-lg overflow-auto max-h-96">
        {JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
};

const renderTextField = (value: string | undefined, title: string) => {
  if (!value) return null;

  return (
    <div className="space-y-2">
      <div className="text-sm font-medium">{title}</div>
      <div className="text-sm bg-muted p-3 rounded-lg whitespace-pre-wrap">
        {value}
      </div>
    </div>
  );
};

/**
 * Legacy search-results view for older sessions. The message_search_results
 * table is no longer written; this surfaces historical rows only and renders
 * nothing when a message has no associated row.
 */
const LegacySearchResults: React.FC<LegacySearchResultsProps> = ({ messageId }) => {
  const { get } = useApi();
  const [searchResults, setSearchResults] = useState<MessageSearchResult | null>(null);
  const [hasSearchResults, setHasSearchResults] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchSearchResults = async () => {
      try {
        const response = await get(`/messages/${messageId}/search-results`);
        if (cancelled) return;
        if (response.data && response.data.data !== null) {
          setSearchResults(response.data);
          setHasSearchResults(true);
        } else {
          setSearchResults(null);
          setHasSearchResults(false);
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching search results:', error);
        setSearchResults(null);
        setHasSearchResults(false);
      }
    };

    fetchSearchResults();

    return () => {
      cancelled = true;
    };
  }, [messageId]);

  if (!hasSearchResults || !searchResults) {
    return null;
  }

  return (
    <>
      <Separator />
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Legacy — Search Results (older sessions)</h3>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="search">Search Data</TabsTrigger>
            <TabsTrigger value="content">Content</TabsTrigger>
            <TabsTrigger value="raw">Raw Data</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            {searchResults.eval_score && renderJsonData(searchResults.eval_score, 'Evaluation Score')}
            {renderTextField(searchResults.query, 'User Query')}
            {renderTextField(searchResults.search_query, 'Search Query')}
            {renderJsonData({
              ready_to_book: searchResults.ready_to_book,
              location_enabled: searchResults.location_enabled,
            }, 'Search Flags')}
          </TabsContent>

          <TabsContent value="search" className="space-y-4">
            {renderJsonData(searchResults.search_results, 'Search Results')}
            {renderJsonData(searchResults.distances, 'Distances')}
          </TabsContent>

          <TabsContent value="content" className="space-y-4">
            {renderTextField(searchResults.system_prompt, 'System Prompt')}
            {renderTextField(searchResults.qualify_prompt, 'Qualify Prompt')}
            {renderTextField(searchResults.page_text, 'Page Text')}
          </TabsContent>

          <TabsContent value="raw" className="space-y-4">
            {renderJsonData(searchResults, 'Complete Search Results')}
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
};

export default LegacySearchResults;
